import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, X, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import type { Tables, TablesInsert, TablesUpdate } from '@/lib/supabase/supabase-types';
import { storageService } from '@/lib/api/services/storage.service';
import { useAIWritingAssistant } from '@/lib/hooks/use-ai-writing-assistant';
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog';

type PortfolioProject = Tables<'portfolio_projects'>;

// Keep in sync with backend `ALLOWED_FILE_TYPES.portfolio-images` in
// `backend/src/services/storage.service.ts`
const ALLOWED_PORTFOLIO_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
] as const;

const MAX_PORTFOLIO_IMAGE_FILE_SIZE = 5 * 1024 * 1024; // 5MB

interface PortfolioProjectFormProps {
  project?: PortfolioProject;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<TablesInsert<'portfolio_projects'>, 'freelancer_profile_id'>) => Promise<void>;
  onUpdate?: (data: { id: string } & TablesUpdate<'portfolio_projects'>) => Promise<void>;
  isLoading?: boolean;
}

export function PortfolioProjectForm({
  project,
  isOpen,
  onClose,
  onSubmit,
  onUpdate,
  isLoading = false,
}: PortfolioProjectFormProps) {
  const { userId } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectUrl, setProjectUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [projectUrlError, setProjectUrlError] = useState<string | undefined>();
  const [titleError, setTitleError] = useState<string | undefined>();
  const [descriptionError, setDescriptionError] = useState<string | undefined>();

  const { generateProjectDescription, isLoading: isAIGenerating, generatingType, data: aiGeneratedData, reset: resetAIData } = useAIWritingAssistant();

  const isEditing = !!project;

  /**
   * Normalize URL by prepending https:// if it starts with www.
   */
  const normalizeProjectUrl = (url: string): string => {
    const trimmed = url.trim();
    if (trimmed.startsWith('www.')) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  /**
   * Validate project title
   * Returns error message if invalid, undefined if valid
   */
  const validateTitle = (title: string): string | undefined => {
    const trimmed = title.trim();
    
    if (!trimmed) {
      return 'Project title is required';
    }
    
    if (trimmed.length < 2) {
      return 'Title must be at least 2 characters long';
    }
    
    if (trimmed.length > 200) {
      return 'Title must be less than 200 characters';
    }
    
    return undefined;
  };

  /**
   * Validate project description
   * Returns error message if invalid, undefined if valid
   */
  const validateDescription = (description: string): string | undefined => {
    const trimmed = description.trim();
    
    if (!trimmed) {
      return 'Project description is required';
    }
    
    if (trimmed.length < 10) {
      return 'Description must be at least 10 characters long';
    }
    
    if (trimmed.length > 5000) {
      return 'Description must be less than 5000 characters';
    }
    
    return undefined;
  };

  /**
   * Validate project URL format
   * Accepts URLs starting with http://, https://, or www.
   * Returns error message if invalid, undefined if valid
   */
  const validateProjectUrl = (url: string): string | undefined => {
    const trimmed = url.trim();
    
    // Empty URLs are allowed (optional field)
    if (!trimmed) {
      return undefined;
    }

    // Check if URL starts with valid prefix
    const startsWithHttp = trimmed.startsWith('http://') || trimmed.startsWith('https://');
    const startsWithWww = trimmed.startsWith('www.');
    
    if (!startsWithHttp && !startsWithWww) {
      return 'Project URL must start with https:// or www.';
    }

    // Normalize URL for validation: if it starts with www., prepend https://
    let urlToValidate = trimmed;
    if (startsWithWww) {
      // Validate www. is followed by a domain with proper structure
      if (trimmed.length <= 4) {
        return 'Please enter a valid project URL';
      }
      
      // For www. URLs, check that there's content after www. and at least one dot
      const afterWww = trimmed.substring(4); // Remove "www."
      const dotCount = (afterWww.match(/\./g) || []).length;
      
      // Must have at least one dot after www. for domain.tld format
      if (dotCount === 0 || afterWww.length < 3) {
        return 'Please enter a valid project URL with a domain extension (e.g., .com, .org)';
      }
      
      urlToValidate = `https://${trimmed}`;
    }

    // Validate URL format using URL constructor
    let urlObj: URL;
    try {
      urlObj = new URL(urlToValidate);
    } catch {
      return 'Please enter a valid project URL';
    }

    // Validate that the URL has a proper hostname
    if (!urlObj.hostname || urlObj.hostname.length < 3) {
      return 'Please enter a valid project URL';
    }

    // Validate protocol (must be http or https)
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return 'Project URL must use http:// or https:// protocol';
    }

    return undefined;
  };

  // Initialize form with project data
  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setDescription(project.description);
      setProjectUrl(project.project_url || '');
      setImageUrl(project.image_url || '');
      // Reset errors when loading existing project
      setTitleError(undefined);
      setDescriptionError(undefined);
      setProjectUrlError(undefined);
    } else {
      // Reset form for new project
      setTitle('');
      setDescription('');
      setProjectUrl('');
      setImageUrl('');
      setTitleError(undefined);
      setDescriptionError(undefined);
      setProjectUrlError(undefined);
    }
  }, [project, isOpen]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Restrict to the same formats allowed by the backend storage service
    if (!ALLOWED_PORTFOLIO_IMAGE_MIME_TYPES.includes(file.type as (typeof ALLOWED_PORTFOLIO_IMAGE_MIME_TYPES)[number])) {
      toast.error('Invalid image format. Allowed formats: JPEG (.jpg, .jpeg), PNG, WEBP');
      return;
    }

    if (file.size > MAX_PORTFOLIO_IMAGE_FILE_SIZE) {
      toast.error(`Image must be smaller than ${MAX_PORTFOLIO_IMAGE_FILE_SIZE / (1024 * 1024)}MB`);
      return;
    }

    setIsUploading(true);

    try {
      // Use backend storage API
      const publicUrl = await storageService.uploadFile('portfolio-images', file);
      setImageUrl(publicUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (imageUrl && userId) {
      try {
        // Extract file path from URL
        // Portfolio images are stored in profile-pictures bucket under portfolio/ folder
        const url = new URL(imageUrl);
        // Match both profile-pictures and portfolio-images in URL (for backwards compatibility)
        const pathMatch = url.pathname.match(/\/(?:profile-pictures|portfolio-images)\/(.+)$/);
        if (pathMatch) {
          const filePath = decodeURIComponent(pathMatch[1]);
          await storageService.deleteFile('portfolio-images', filePath);
        }
      } catch (error) {
        console.error('Error removing image:', error);
        // Don't show error to user as this is not critical
      }
    }
    setImageUrl('');
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    // Validate on change for real-time feedback
    const error = validateTitle(value);
    setTitleError(error);
  };

  const handleTitleBlur = () => {
    const error = validateTitle(title);
    setTitleError(error);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    // Validate on change for real-time feedback
    const error = validateDescription(value);
    setDescriptionError(error);
  };

  const handleDescriptionBlur = () => {
    const error = validateDescription(description);
    setDescriptionError(error);
  };

  const handleProjectUrlChange = (value: string) => {
    setProjectUrl(value);
    // Validate on change for real-time feedback
    const error = validateProjectUrl(value);
    setProjectUrlError(error);
  };

  const handleProjectUrlBlur = () => {
    const error = validateProjectUrl(projectUrl);
    setProjectUrlError(error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const titleErr = validateTitle(title);
    const descriptionErr = validateDescription(description);
    const urlError = validateProjectUrl(projectUrl);

    // Set all errors
    setTitleError(titleErr);
    setDescriptionError(descriptionErr);
    setProjectUrlError(urlError);

    // If any validation fails, show error and return
    if (titleErr || descriptionErr || urlError) {
      if (titleErr) {
        toast.error(titleErr);
      } else if (descriptionErr) {
        toast.error(descriptionErr);
      } else if (urlError) {
        toast.error(urlError);
      }
      return;
    }

    try {
      // Normalize project URL before saving (prepend https:// to www. URLs)
      const normalizedProjectUrl = projectUrl.trim() 
        ? normalizeProjectUrl(projectUrl.trim())
        : null;

      if (isEditing && onUpdate && project) {
        await onUpdate({
          id: project.id,
          title: title.trim(),
          description: description.trim(),
          project_url: normalizedProjectUrl,
          image_url: imageUrl || null,
        });
      } else {
        await onSubmit({
          title: title.trim(),
          description: description.trim(),
          project_url: normalizedProjectUrl,
          image_url: imageUrl || null,
        });
      }
      onClose();
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error saving project:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold">
            {isEditing ? 'Edit Project' : 'Add New Project'}
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            {isEditing ? 'Update your project details' : 'Add a new project to showcase your AI expertise'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* Project Image */}
          <div className="space-y-3">
            <Label>Project Image (Optional)</Label>
            {imageUrl ? (
              <div className="relative">
                <div className="aspect-video w-full overflow-hidden rounded-lg border">
                  <img
                    src={imageUrl}
                    alt="Project preview"
                    className="h-full w-full object-cover"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveImage}
                  disabled={isUploading}
                  className="absolute right-2 top-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-3 w-3 mr-1" />
                  Remove
                </Button>
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
              </div>
            ) : (
              <Label
                htmlFor="image-upload"
                className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to upload an image (PNG, JPG up to 5MB)
                </span>
                {isUploading && (
                  <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground mt-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </p>
                )}
                <Input
                  id="image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                />
              </Label>
            )}
          </div>

          {/* Project Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Project Title *</Label>
            <Input
              id="title"
              placeholder="AI-Powered Customer Support Bot"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              onBlur={handleTitleBlur}
              required
              maxLength={200}
              className={titleError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {titleError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {titleError}
              </p>
            )}
            <p className="text-xs text-slate-500">{title.length}/200 characters</p>
          </div>

          {/* Project Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="description">Description *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsAIDialogOpen(true)}
                disabled={isAIGenerating}
                className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
              >
                {isAIGenerating && generatingType === 'project_description' ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3 mr-1" />
                    Write description with AI
                  </>
                )}
              </Button>
            </div>
            <AIAssistantDialog
              open={isAIDialogOpen}
              onOpenChange={(open) => {
                setIsAIDialogOpen(open);
                if (!open) {
                  resetAIData();
                }
              }}
              onGenerate={async (currentText, options) => {
                const result = await generateProjectDescription(currentText ?? description, {
                  ...options,
                  projectTitle: title,
                  projectUrl: projectUrl,
                });
                return result?.text || null;
              }}
              onAccept={(text) => {
                setDescription(text);
                // Validate the AI-generated description
                const error = validateDescription(text);
                setDescriptionError(error);
                resetAIData();
              }}
              generatedText={aiGeneratedData?.type === 'project_description' ? aiGeneratedData.text : null}
              isLoading={isAIGenerating && generatingType === 'project_description'}
              type="project_description"
              currentText={description}
            />
            <Textarea
              id="description"
              placeholder="Describe your project in detail. Include the problem you solved, your approach, technologies used, and the impact or results achieved..."
              value={description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              onBlur={handleDescriptionBlur}
              required
              rows={10}
              maxLength={5000}
              className={`min-h-[200px] resize-y ${descriptionError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            />
            {descriptionError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {descriptionError}
              </p>
            )}
            <p className="text-xs text-slate-500">{description.length}/5000 characters</p>
          </div>

          {/* Project URL */}
          <div className="space-y-2">
            <Label htmlFor="projectUrl">Project URL (Optional)</Label>
            <Input
              id="projectUrl"
              type="url"
              placeholder="https://github.com/username/project or https://myproject.com"
              value={projectUrl}
              onChange={(e) => handleProjectUrlChange(e.target.value)}
              onBlur={handleProjectUrlBlur}
              className={projectUrlError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {projectUrlError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {projectUrlError}
              </p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-3 sm:gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="w-full sm:w-auto rounded-[48px] order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isUploading || !title.trim() || !description.trim()}
              className="w-full sm:w-auto rounded-[48px] bg-[#4669ed] hover:bg-[#3654d4] order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update Project' : 'Add Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
